/** One scoped, model-free setup read. Fetching is separate from production/Ladle rendering. */
import { useQuery } from '@tanstack/react-query'
import { shopifyQueryPolicies } from '../../queries/shopifyQueryPolicies'
import { useAppBridge } from '@shopify/app-bridge-react'
import { currentInsightSetupsSchema } from '@testfeed/shopify-contracts'
import { shopifyApiClient } from '../../shopifyApiClient'
import { shopifyQueryKey, useShopifyAppContext } from '../../ShopifyAppContext'
import type { StudySeedOutcome } from '../../hooks/studySeedOutcome'
import type { InsightStudyConfirmation } from '../../hooks/study-builder/studyAuthoringClient'
import type { InsightsLifecycle } from '../../pages/insights.page'
import type { RenderPage } from '../../insightRenderPresentation'
import { InsightOpportunities, type InsightSetupsState } from './InsightOpportunities'

export type TestInsightSetup = {
  resolve: (batchId: string, productId: string, signal?: AbortSignal) => Promise<InsightStudyConfirmation>
  submit: (confirmation: InsightStudyConfirmation, signal?: AbortSignal) => Promise<StudySeedOutcome>
}

export function InsightSetupsSection({
  page,
  revision,
  onTestSetup,
  lifecycle,
}: {
  page: RenderPage
  revision: string
  onTestSetup: TestInsightSetup
  lifecycle: InsightsLifecycle
}) {
  const shopify = useAppBridge()
  const scope = useShopifyAppContext()
  const enabled = Boolean(scope.orgId?.trim() && scope.connectionId?.trim() && revision)
  const query = useQuery({
    ...shopifyQueryPolicies.insightSetups,
    queryKey: shopifyQueryKey(scope.shopDomain, scope.orgId, scope.connectionId, ['opportunity-setups', revision]),
    enabled,
    queryFn: async () => {
      const response = await shopifyApiClient(shopify).api['study-builder']['opportunity-setups'].$get()
      if (!response.ok) throw new Error(`Ready-to-test setups could not be loaded (${response.status})`)
      return currentInsightSetupsSchema.parse(await response.json()).setups
    },
  })
  let state: InsightSetupsState
  if (!enabled || query.isError) state = { status: 'error' }
  else if (query.isPending) state = { status: 'loading' }
  else state = { status: 'ready', setups: query.data }
  return (
    <InsightOpportunities
      key={`${scope.shopDomain}:${scope.orgId}:${scope.connectionId}`}
      page={page}
      revision={revision}
      state={state}
      onTestSetup={onTestSetup}
      lifecycle={lifecycle}
    />
  )
}
